/* Audio — PPV voice notes (bigdayz.new ADR 0068). After a chat PPV the AI may send a
   free audio in the model's voice. Three sections in the rail:
     Voices       — pick a stock or custom voice (▶ preview), clone a new one.
     Audio sends  — on/off, cooldown, languages she speaks (English only for now), and the
                    normal / hot tone notes folded under Advanced.
     Live test    — type her text, hear it in her voice (the AI only adds delivery tags;
                    the tagged script is shown to super admins only). Opens on Hot.
   Settings save on their own, like the other compact pages. Every live test takes one
   audio send from the account's quota, the same counter real sends use. Voices are
   labels only: the provider never shows up on this page (white-label). */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, AudioPolicy, AudioPolicyBody, Voice, VoiceNoteQuota, VoicesResponse } from '../../../../api/client';
import { AdvancedToggle, Badge, Btn, Card, ErrorLine, Icon, InfoLine, Loading, NumInput, PageFrame, Row, SaveStatus, Section, SectionRail, Seg, Spin, Stack, TextArea, Toggle, Warn, type RailRow } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { patchNow, usePolicy, type Policy } from '../usePolicy';
import {
  AVAILABLE_LANGUAGES, EMPTY_POLICY, EMPTY_TEST, LANGUAGES, MAX_COOLDOWN, MAX_TEST_TEXT, MAX_TONE_NOTES, SECS,
  languageName, offReason, policyProblem, selectVoice, selectedKey, testBody, voiceKey,
  type Sec, type TestDraft,
} from './model';
import { CloneCard, PreviewButton, QuotaBar, RunCard, type Run } from './shared';
import { capStyle, chip, emptyStyle, fieldCol, iconBtn, linkBtn } from './styles';
import { usePreview } from './usePreview';

const POLL_MS = 5000;

const bodyOf = ({ exists: _e, updated_at: _u, ...b }: AudioPolicy): AudioPolicyBody => b;

export function AudioPage({ account }: { account: Account }) {
  return <AudioSettings key={account.account_id} accountId={account.account_id} />;
}

function AudioSettings({ accountId }: { accountId: string }) {
  const { t } = useTranslation('assistantCompact');
  const [params, setParams] = useSearchParams();
  const sec: Sec = SECS.find((s) => s === params.get('s')) ?? 'notes';
  const select = (s: string) => { const n = new URLSearchParams(params); n.set('s', s); setParams(n, { replace: true }); };

  const policy = usePolicy<AudioPolicyBody>(
    () => api.getAudioPolicy(accountId).then(bodyOf),
    (b) => api.putAudioPolicy(accountId, b).then(() => undefined),
    EMPTY_POLICY,
    accountId,
    600,
    (b) => {
      const p = policyProblem(b);
      return p === 'cooldown' ? t('au.n.cooldownBad', { max: MAX_COOLDOWN }) : p === 'tone' ? t('au.n.toneTooLong', { max: MAX_TONE_NOTES }) : null;
    },
  );

  // ── Voices (polled while a clone is processing) ─────────────────
  const [voices, setVoices] = useState<VoicesResponse | null>(null);
  const [voicesFailed, setVoicesFailed] = useState(false);
  const loadVoices = useCallback(() => api.getVoices(accountId).then(
    (v) => { setVoices(v); setVoicesFailed(false); },
    () => setVoicesFailed(true),
  ), [accountId]);
  useEffect(() => { loadVoices(); }, [loadVoices]);
  const processing = !!voices?.voices.some((v) => v.status === 'processing');
  useEffect(() => {
    if (!processing) return;
    const h = window.setInterval(loadVoices, POLL_MS);
    return () => window.clearInterval(h);
  }, [processing, loadVoices]);

  // ── Quota ───────────────────────────────────────────────────────
  const [quota, setQuota] = useState<VoiceNoteQuota | null>(null);
  useEffect(() => { api.getVoiceNoteQuota(accountId).then(setQuota, () => setQuota(null)); }, [accountId]);

  const b = policy.body;
  const ready = policy.state === 'ready';
  const current = voices?.voices.find((v) => voiceKey(v) === selectedKey(b));
  const rail: RailRow[] = [
    { id: 'voice', label: t('au.s.voice'), meta: current?.label },
    {
      id: 'notes', label: t('au.s.notes'),
      on: ready ? b.voice_notes_enabled : null,
      onToggle: (v) => patchNow(policy, { voice_notes_enabled: v }),
    },
    { id: 'test', label: t('au.s.test'), meta: quota ? `${quota.used}/${quota.allowed}` : undefined, metaTitle: quota ? t('au.q.tip', { ...quota }) : undefined },
  ];

  return (
    <PageFrame
      rail={<SectionRail heading={t('au.rail')} items={rail} active={sec} onSelect={select} width={210} />}
      headline={t(`au.s.${sec}`)}
      desc={t(`au.d.${sec}`)}
      right={<SaveStatus status={policy.auto.status} error={policy.auto.error} onRetry={policy.auto.retry} idleLabel />}
    >
      {policy.state === 'loading' && <Loading />}
      {policy.state === 'failed' && <ErrorLine>{t('loadFailed')}</ErrorLine>}
      {ready && sec !== 'test' && <StatusLine policy={b} voices={voices?.voices ?? null} onGo={select} />}
      {ready && sec === 'voice' && (
        <VoiceSection accountId={accountId} policy={policy} voices={voices} failed={voicesFailed} reload={loadVoices} setVoices={setVoices} />
      )}
      {ready && sec === 'notes' && <NotesSection policy={policy} />}
      {ready && sec === 'test' && (
        <TestSection accountId={accountId} policy={b} voice={current ?? null} serviceReady={voices?.voice_service_ready ?? true} quota={quota} setQuota={setQuota} onGo={select} />
      )}
    </PageFrame>
  );
}

/* ── Status ─────────────────────────────────────────────────────── */
function StatusLine({ policy, voices, onGo }: { policy: AudioPolicyBody; voices: Voice[] | null; onGo: (s: Sec) => void }) {
  const { t } = useTranslation('assistantCompact');
  const off = offReason(policy, voices);
  if (!off) {
    const c = policy.voice_note_cooldown;
    return <InfoLine>{c ? t('au.on.cooldown', { count: c }) : t('au.on.every')}</InfoLine>;
  }
  const go: Sec = off === 'disabled' || off === 'noLanguages' ? 'notes' : 'voice';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <InfoLine>{t(`au.off.${off}`)}</InfoLine>
      <button type="button" onClick={() => onGo(go)} style={linkBtn}>{t(`au.off.go.${go}`)}</button>
    </div>
  );
}

/* ── Voice ──────────────────────────────────────────────────────── */
function VoiceSection({ accountId, policy, voices, failed, reload, setVoices }: {
  accountId: string;
  policy: Policy<AudioPolicyBody>;
  voices: VoicesResponse | null;
  failed: boolean;
  reload: () => Promise<void>;
  setVoices: (fn: (v: VoicesResponse | null) => VoicesResponse | null) => void;
}) {
  const { t, i18n } = useTranslation('assistantCompact');
  const preview = usePreview((v) => api.getVoicePreviewUrl(accountId, v.kind, v.id));
  const [cloning, setCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const sel = selectedKey(policy.body);

  if (failed && !voices) return <ErrorLine>{t('au.v.loadFailed')}</ErrorLine>;
  if (!voices) return <Loading />;

  const stock = voices.voices.filter((v) => v.kind === 'stock');
  const custom = voices.voices.filter((v) => v.kind === 'custom');

  const remove = async (v: Voice) => {
    if (!window.confirm(t('au.v.deleteConfirm', { name: v.label }))) return;
    setDeleting(v.id);
    setError(null);
    try {
      await api.deleteCustomVoice(accountId, v.id);
      // The server switched off any policy pointing at it; mirror that in the draft.
      if (policy.body.custom_voice_id === v.id) policy.patch({ custom_voice_id: null });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('au.v.deleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  const row = (v: Voice) => {
    const key = voiceKey(v);
    const on = key === sel;
    const pickable = v.status === 'ready';
    return (
      <div
        key={key}
        role="radio"
        aria-checked={on}
        aria-disabled={!pickable}
        tabIndex={pickable ? 0 : -1}
        onClick={() => pickable && patchNow(policy, selectVoice(v))}
        onKeyDown={(e) => { if (pickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); patchNow(policy, selectVoice(v)); } }}
        className={pickable ? 'cpt-hover-row' : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 14px', minHeight: 46, cursor: pickable ? 'pointer' : 'default', background: on ? 'color-mix(in srgb, var(--om-accent) 8%, transparent)' : undefined }}
      >
        <span style={{ width: 14, height: 14, flex: 'none', borderRadius: 99, border: `1.5px solid ${on ? C.purple : C.borderHi}`, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pickable ? 1 : 0.5 }}>
          {on && <span style={{ width: 7, height: 7, borderRadius: 99, background: C.purple }} />}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <span style={{ font: f(600, 13), color: pickable ? C.text : C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label}</span>
            {v.is_default && <Badge>{t('au.v.default')}</Badge>}
          </span>
          <span style={{ font: f(400, 11.5), color: C.muted, display: 'flex', alignItems: 'center', gap: 5 }}>
            {v.status === 'processing' ? <><Spin size={10} />{t('au.v.processing')}</>
              : v.status === 'failed' ? <><Icon name="alert-circle" size={11} color={C.purplePale} /><span style={{ color: C.purplePale }}>{t('au.v.failed')}</span></>
              : v.kind === 'stock' ? (v.languages.length ? v.languages.map((l) => languageName(l, i18n.language)).join(', ') : t('au.v.stock'))
              : v.owned ? t('au.v.custom') : t('au.v.someoneElses')}
          </span>
        </span>
        {pickable && voices.voice_service_ready && (
          <PreviewButton state={preview.stateOf(v)} onClick={(e) => { e.stopPropagation(); preview.toggle(v); }} />
        )}
        {v.kind === 'custom' && v.owned && (
          <button type="button" title={t('au.v.delete')} disabled={deleting === v.id} onClick={(e) => { e.stopPropagation(); remove(v); }} className="cpt-hover-text" style={iconBtn}>
            {deleting === v.id ? <Spin size={12} /> : <Icon name="trash-2" size={13} />}
          </button>
        )}
      </div>
    );
  };

  return (
    <Stack gap={16}>
      {!sel && <Warn>{t('au.v.none')}</Warn>}
      {preview.error && <Warn>{preview.error}</Warn>}
      {error && <Warn>{error}</Warn>}
      <Section title={t('au.v.stockTitle')} hint={t('au.v.stockHint')}>
        <Card>{stock.length ? stock.map(row) : <div style={emptyStyle}>{t('au.v.noStock')}</div>}</Card>
      </Section>
      <Section
        title={t('au.v.customTitle')}
        hint={t('au.v.customHint', { n: voices.custom_count, max: voices.custom_limit })}
        right={!cloning && <Btn kind="tint" icon="mic" disabled={!voices.can_clone} title={voices.can_clone ? undefined : t(voices.voice_service_ready ? 'au.c.limit' : 'au.c.unavailable', { max: voices.custom_limit })} onClick={() => setCloning(true)}>{t('au.c.open')}</Btn>}
      >
        {cloning && (
          <CloneCard
            clone={(label, files) => api.cloneVoice(accountId, label, files)}
            onCancel={() => setCloning(false)}
            onCreated={(v) => {
              setCloning(false);
              setVoices((cur) => cur && { ...cur, voices: [...cur.voices.filter((x) => x.kind === 'stock'), v, ...cur.voices.filter((x) => x.kind === 'custom')], custom_count: cur.custom_count + 1, can_clone: cur.custom_count + 1 < cur.custom_limit });
            }}
          />
        )}
        {custom.length ? <Card>{custom.map(row)}</Card> : !cloning && <Card><div style={emptyStyle}>{t('au.v.noCustom')}</div></Card>}
      </Section>
      {!voices.voice_service_ready && <InfoLine>{t('au.v.serviceOff')}</InfoLine>}
    </Stack>
  );
}

/* ── Audio sends ─────────────────────────────────────────────────── */
function NotesSection({ policy }: { policy: Policy<AudioPolicyBody> }) {
  const { t, i18n } = useTranslation('assistantCompact');
  const [adv, setAdv] = useState(false);
  const b = policy.body;
  const toggleLang = (code: string) => {
    const has = b.allowed_languages.includes(code);
    patchNow(policy, { allowed_languages: has ? b.allowed_languages.filter((x) => x !== code) : [...b.allowed_languages, code] });
  };
  return (
    <Stack gap={16}>
      <Card>
        <Row label={t('au.n.enabled')} help={t('au.n.enabledHelp')}>
          <Toggle on={b.voice_notes_enabled} onChange={(v) => patchNow(policy, { voice_notes_enabled: v })} />
        </Row>
        <div style={{ opacity: b.voice_notes_enabled ? 1 : 0.45, transition: 'opacity .15s' }}>
        <Row label={t('au.n.cooldown')} help={t('au.n.cooldownHelp')}>
          <NumInput value={b.voice_note_cooldown} onChange={(v) => policy.patch({ voice_note_cooldown: v })} int allowEmpty min={0} max={MAX_COOLDOWN} placeholder="0" width={64} />
          <span>{t('au.n.cooldownUnit')}</span>
        </Row>
        <Row stack label={t('au.n.languages')} help={t('au.n.languagesHelp')}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[...LANGUAGES, ...b.allowed_languages.filter((x) => !(LANGUAGES as readonly string[]).includes(x))].map((code) => {
              const on = b.allowed_languages.includes(code);
              // Only English can be turned on for now; one picked earlier can still be turned off.
              const locked = !on && !AVAILABLE_LANGUAGES.includes(code);
              return (
                <button key={code} type="button" aria-pressed={on} disabled={locked} title={locked ? t('au.n.comingSoon') : undefined} onClick={() => toggleLang(code)} className={locked ? undefined : 'cpt-hover'} style={{ ...chip, cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.4 : 1, borderColor: on ? C.tintLine45 : C.border, background: on ? C.tint16 : C.input, color: on ? C.text : C.sub }}>
                  <Icon name={on ? 'check' : locked ? 'lock' : 'plus'} size={11} color={on ? C.purpleLt : C.muted} />
                  {languageName(code, i18n.language)}
                </button>
              );
            })}
          </div>
          {!b.allowed_languages.length && <Warn>{t('au.n.noLanguages')}</Warn>}
        </Row>
        </div>
      </Card>
      <AdvancedToggle open={adv} onToggle={() => setAdv((v) => !v)} hint={t('au.n.advancedHint')} />
      {adv && (
        <Card>
          <Row stack label={t('au.n.normal')} help={t('au.n.normalHelp')}>
            <TextArea value={b.normal_tone_notes} onChange={(v) => policy.patch({ normal_tone_notes: v })} placeholder={t('au.n.normalPh')} height={70} />
          </Row>
          <Row stack label={t('au.n.hot')} help={t('au.n.hotHelp')}>
            <TextArea value={b.hot_tone_notes} onChange={(v) => policy.patch({ hot_tone_notes: v })} placeholder={t('au.n.hotPh')} height={70} />
          </Row>
        </Card>
      )}
    </Stack>
  );
}

/* ── Live test ───────────────────────────────────────────────────── */
let runSeq = 0;

function TestSection({ accountId, policy, voice, serviceReady, quota, setQuota, onGo }: {
  accountId: string;
  policy: AudioPolicyBody;
  voice: Voice | null;
  serviceReady: boolean;
  quota: VoiceNoteQuota | null;
  setQuota: (q: VoiceNoteQuota) => void;
  onGo: (s: Sec) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const [draft, setDraft] = useState<TestDraft>(EMPTY_TEST);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const patch = (p: Partial<TestDraft>) => setDraft((d) => ({ ...d, ...p }));

  const blocker =
    !voice ? 'noVoice'
      : voice.status !== 'ready' ? 'voiceNotReady'
      : quota && quota.remaining <= 0 ? 'quota'
      : !serviceReady ? 'service'
      : null;

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await api.testVoiceNote(accountId, testBody(policy, draft));
      setQuota(result.quota);
      setRuns((r) => [{ id: ++runSeq, at: new Date(), register: draft.voice_register, voice: voice?.label ?? '', result }, ...r].slice(0, 10));
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(msg === 'REQUEST_TIMEOUT' ? t('au.t.timeout') : msg || t('au.t.failed'));
      // A refused or failed try may or may not have been charged — show the real count.
      api.getVoiceNoteQuota(accountId).then(setQuota, () => undefined);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Stack gap={16}>
      <QuotaBar quota={quota} />
      <Card clip={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={capStyle}>{t('au.t.register')}</span>
          <Seg
            options={[{ id: 'normal', label: t('au.t.normal') }, { id: 'hot', label: t('au.t.hot') }]}
            value={draft.voice_register}
            onChange={(v) => patch({ voice_register: v })}
          />
          <span style={{ font: f(400, 11.5), color: C.muted }}>{t(draft.voice_register === 'hot' ? 'au.t.hotHint' : 'au.t.normalHint')}</span>
        </div>
        <label style={fieldCol}>
          <span style={capStyle}>{t('au.t.text')}</span>
          <TextArea value={draft.text} onChange={(v) => patch({ text: v })} placeholder={t('au.t.textPh')} height={70} />
          <span style={{ font: f(400, 11.5), color: C.muted }}>{t('au.t.textHelp')}</span>
          {draft.text.length > MAX_TEST_TEXT && <Warn>{t('au.t.textTooLong', { max: MAX_TEST_TEXT })}</Warn>}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Btn kind="primary" icon={running ? undefined : 'play-circle'} height={30} disabled={running || !!blocker || !draft.text.trim() || draft.text.length > MAX_TEST_TEXT} onClick={run}>
            {running ? <><Spin size={11} />{t('au.t.running')}</> : t('au.t.run')}
          </Btn>
          {!blocker && (
            <span style={{ font: f(400, 11.5), color: C.muted }}>
              {t('au.t.cost', { voice: voice?.label ?? '' })}
            </span>
          )}
          {blocker && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Warn>{t(`au.t.block.${blocker}`)}</Warn>
              {(blocker === 'noVoice' || blocker === 'voiceNotReady') && (
                <button type="button" style={linkBtn} onClick={() => onGo('voice')}>{t('au.off.go.voice')}</button>
              )}
            </span>
          )}
        </div>
        {error && <Warn>{error}</Warn>}
      </Card>
      {runs.length > 0 && (
        <Section title={t('au.t.results')} hint={t('au.t.resultsHint')}>
          <Stack gap={10}>{runs.map((r) => <RunCard key={r.id} run={r} />)}</Stack>
        </Section>
      )}
    </Stack>
  );
}
